import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import styles from './physicalCards.module.scss';
import { config } from '../../api/config';
import { Input, Select, SubmitActions } from '../../components';
import { OWNER_TYPE_PRIVATE } from './physicalCards.helpers';

const PhysicalCardForm = ({
  mode,
  values,
  errors,
  onChange,
  onOwnerQueryChange,
  onOwnerSelect,
  onSubmit,
  loading,
  submitDisabled,
  ownerResults,
  ownerSearchLoading,
  ownerSearchError,
  infoRows,
}) => {
  const isEdit = mode === 'edit';
  const hasDiscountPlan = Boolean(values.membership_id);

  return (
    <form onSubmit={onSubmit} method="POST">
      {isEdit && infoRows && infoRows.length > 0 && (
        <div className={styles.cardInfoContent}>
          {infoRows.map(({ title, value }) => (
            <div key={title} className={styles.cardInfoInner}>
              <span className={styles.cardInfoTitle}>{title}</span>
              <span className={styles.cardInfoValue}>{value}</span>
            </div>
          ))}
        </div>
      )}

      <div className={cn(styles.formFields, isEdit && styles.formFieldsEdit)}>
        {!isEdit && (
          <div className={styles.field}>
            <Input
              name="number"
              label="Kortelės ID / RFID"
              type="text"
              value={values.number}
              onChange={onChange}
              required
            />
            {errors.number && (
              <span className={styles.fieldError}>{errors.number}</span>
            )}
          </div>
        )}

        <div className={styles.field}>
          <Select
            name="status"
            label="Statusas"
            options={values.statusOptions}
            selectedValue={values.status}
            onChange={onChange}
            labelContainerClassName={styles.selectLabel}
          />
          {errors.status && (
            <span className={styles.fieldError}>{errors.status}</span>
          )}
        </div>

        <div className={styles.field}>
          <Select
            name="owner_type"
            label="Savininko tipas"
            options={values.ownerTypeOptions}
            selectedValue={values.owner_type}
            onChange={onChange}
            labelContainerClassName={styles.selectLabel}
          />
          {errors.owner_type && (
            <span className={styles.fieldError}>{errors.owner_type}</span>
          )}
        </div>

        <div className={styles.field}>
          <Input
            name="owner"
            label="Savininkas"
            type="text"
            value={values.ownerQuery}
            onChange={onOwnerQueryChange}
            required
            isAutocomplete
            results={ownerResults}
            autocompleteLoading={ownerSearchLoading}
            autocompleteError={ownerSearchError}
            onAutcompleteSelect={onOwnerSelect}
            placeholder={
              Number(values.owner_type) === OWNER_TYPE_PRIVATE
                ? 'Ieškoti pagal telefoną'
                : 'Ieškoti pagal įmonės pavadinimą'
            }
          />
          {errors.owner && (
            <span className={styles.fieldError}>{errors.owner}</span>
          )}
        </div>

        {!isEdit && (
          <>
            <div className={styles.field}>
              <Select
                name="membership_id"
                label="Nuolaidos planas"
                options={values.membershipOptions}
                selectedValue={values.membership_id}
                onChange={onChange}
                labelContainerClassName={styles.selectLabel}
              />
            </div>

            {hasDiscountPlan && (
              <>
                <div className={styles.field}>
                  <div className={styles.dateTimeRow}>
                    <Input
                      name="discount_valid_from"
                      label="Galioja nuo"
                      type="date"
                      value={values.discount_valid_from}
                      onChange={onChange}
                      max="2999-12-31"
                      required
                    />
                    <Input
                      name="discount_valid_from_time"
                      label="Laikas"
                      type="time"
                      value={values.discount_valid_from_time}
                      onChange={onChange}
                    />
                  </div>
                  {errors.discount_valid_from && (
                    <span className={styles.fieldError}>
                      {errors.discount_valid_from}
                    </span>
                  )}
                </div>

                <div className={styles.field}>
                  <div className={styles.dateTimeRow}>
                    <Input
                      name="discount_valid_to"
                      label="Galioja iki"
                      type="date"
                      value={values.discount_valid_to}
                      onChange={onChange}
                      max="2999-12-31"
                    />
                    <Input
                      name="discount_valid_to_time"
                      label="Laikas"
                      type="time"
                      value={values.discount_valid_to_time}
                      onChange={onChange}
                      disabled={!values.discount_valid_to}
                    />
                  </div>
                  {errors.discount_valid_to && (
                    <span className={styles.fieldError}>
                      {errors.discount_valid_to}
                    </span>
                  )}
                </div>
              </>
            )}

            <div className={styles.field}>
              <Input
                name="initial_balance"
                label="Pradinis likutis (€)"
                type="number"
                value={values.initial_balance}
                onChange={onChange}
                min="0"
                step="0.01"
              />
              {errors.initial_balance && (
                <span className={styles.fieldError}>
                  {errors.initial_balance}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <SubmitActions
        back={config.APP_PATHS.physicalCards}
        loading={loading}
        disabled={submitDisabled}
        cancel="Atgal"
        className={isEdit ? styles.submitActionsEdit : undefined}
      />
    </form>
  );
};

PhysicalCardForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']),
  values: PropTypes.shape({
    number: PropTypes.string.isRequired,
    owner_type: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
    owner_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ownerQuery: PropTypes.string.isRequired,
    membership_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    discount_valid_from: PropTypes.string,
    discount_valid_from_time: PropTypes.string,
    discount_valid_to: PropTypes.string,
    discount_valid_to_time: PropTypes.string,
    initial_balance: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    status: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
    statusOptions: PropTypes.array.isRequired,
    ownerTypeOptions: PropTypes.array.isRequired,
    membershipOptions: PropTypes.array,
  }).isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onOwnerQueryChange: PropTypes.func.isRequired,
  onOwnerSelect: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  submitDisabled: PropTypes.bool,
  ownerResults: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    })
  ),
  ownerSearchLoading: PropTypes.bool,
  ownerSearchError: PropTypes.string,
  infoRows: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.node.isRequired,
    })
  ),
};

PhysicalCardForm.defaultProps = {
  mode: 'create',
  submitDisabled: true,
  ownerResults: [],
  ownerSearchLoading: false,
  ownerSearchError: '',
  infoRows: [],
};

export default PhysicalCardForm;
